import React from 'react';
import { render, Component } from 'preact';
import { getApiUrlByName } from 'project/helpers';
import { apiNames, notificationTypes } from 'project/general';
import { baseApi } from 'project/api';

const GET_QUESTION_URL = getApiUrlByName(apiNames.GET_ASK_ASH_QUESTION);
const TRANSITION_MS = 250;
const FADE_IN_DELAY_MS = 40;
const RESULTS_HISTORY_STORAGE_KEY = 'nas.askAsh.resultsHistory';

export class AskAshWidget extends Component {
    constructor(props) {
        super(props);

        this.containerRef = null;
        this.hasAutoScrolled = false;
        this.skipAutoLoadBecauseUrlParam = false;
        this.transitionTimer = null;
        this.fadeInDelayTimer = null;

        this.initialState = {
            open: false,
            started: false,
            history: [],
            loading: false,
            error: null,
            displayedNode: null,
            transitioning: false,
            fadeState: 'idle',
            openedFromResultsPage: false,
        };

        this.state = { ...this.initialState };
    }

    componentDidMount() {
        const questionId = this.getQuestionIdFromUrl();

        // Continue supporting normal Ask Ash links that deliberately
        // open a question using ?questionId=.
        //
        // The results-page Back button does not use the query string.
        if (questionId && !this.isRestartVariant()) {
            this.openQuestion(questionId);
            return;
        }

        if (
            this.state.open &&
            this.state.history.length === 0 &&
            this.state.started
        ) {
            this.loadNode();
        }
    }

    componentDidUpdate(_, prevState) {
        if (this.skipAutoLoadBecauseUrlParam) {
            this.skipAutoLoadBecauseUrlParam = false;
            return;
        }

        if (
            this.state.open &&
            this.state.started &&
            this.state.history.length === 0 &&
            (!prevState.open || !prevState.started)
        ) {
            this.loadNode();
        }
    }

    componentWillUnmount() {
        this.clearTransitionTimers();
    }

    scrollToQuestionArea = () => {
        if (!this.containerRef || typeof window === 'undefined') {
            return;
        }

        const yOffset = -20;

        const y =
            this.containerRef.getBoundingClientRect().top +
            window.pageYOffset +
            yOffset;

        window.scrollTo({
            top: y,
            behavior: 'smooth',
        });
    };

    clearTransitionTimers = () => {
        if (this.transitionTimer) {
            clearTimeout(this.transitionTimer);
            this.transitionTimer = null;
        }

        if (this.fadeInDelayTimer) {
            clearTimeout(this.fadeInDelayTimer);
            this.fadeInDelayTimer = null;
        }
    };

    getStepElement = () => {
        if (typeof window === 'undefined') {
            return null;
        }

        return document.querySelector(
            '[data-dc-ask-ash-wizard][data-nas-ga-component-step]'
        );
    };

    buildComponentStepPath = history => {
        if (!history || !history.length) {
            return '';
        }

        const rawSteps = history
            .map(node => node?.dataComponentStep)
            .filter(
                step =>
                    step != null &&
                    String(step).trim() !== ''
            )
            .map(step => String(step).trim());

        if (!rawSteps.length) {
            return '';
        }

        const cleanSteps = [];

        rawSteps.forEach(step => {
            const parts = step
                .split('>')
                .map(part => part.trim())
                .filter(part => part !== '');

            parts.forEach(part => {
                if (!cleanSteps.includes(part)) {
                    cleanSteps.push(part);
                }
            });
        });

        return cleanSteps.join('>');
    };

    setComponentStep = history => {
        const element = this.getStepElement();

        if (!element) {
            return;
        }

        const value = this.buildComponentStepPath(history);

        element.setAttribute(
            'data-nas-ga-component-step',
            value
        );
    };

    getQuestionIdFromUrl = () => {
        if (typeof window === 'undefined') {
            return null;
        }

        const params = new URLSearchParams(
            window.location.search || ''
        );

        const questionId = params.get('questionId');

        return questionId && questionId.trim()
            ? questionId.trim()
            : null;
    };

    getReferringQuestionId = () => {
        const propQuestionId =
            this.props.referringQuestionId;

        if (
            propQuestionId != null &&
            String(propQuestionId).trim() !== ''
        ) {
            return String(propQuestionId).trim();
        }

        if (typeof document === 'undefined') {
            return null;
        }

        const element = document.querySelector(
            '[data-referring-question-id]'
        );

        const attributeQuestionId =
            element?.getAttribute(
                'data-referring-question-id'
            );

        return attributeQuestionId &&
            attributeQuestionId.trim()
            ? attributeQuestionId.trim()
            : null;
    };

    normaliseUrl = url => {
        if (!url || typeof window === 'undefined') {
            return null;
        }

        try {
            const parsedUrl = new URL(
                url,
                window.location.origin
            );

            return `${parsedUrl.pathname}${parsedUrl.search}`;
        } catch (err) {
            return null;
        }
    };

    saveResultsHistory = resultsUrl => {
        if (
            typeof window === 'undefined' ||
            !Array.isArray(this.state.history) ||
            !this.state.history.length
        ) {
            return;
        }

        try {
            window.sessionStorage.setItem(
                RESULTS_HISTORY_STORAGE_KEY,
                JSON.stringify({
                    resultUrl:
                        this.normaliseUrl(resultsUrl),
                    history: this.state.history,
                })
            );
        } catch (err) {
            // Results navigation can continue if storage is unavailable.
        }
    };

    getResultsHistory = () => {
        if (typeof window === 'undefined') {
            return null;
        }

        try {
            const storedValue =
                window.sessionStorage.getItem(
                    RESULTS_HISTORY_STORAGE_KEY
                );

            if (!storedValue) {
                return null;
            }

            const stored = JSON.parse(storedValue);
            const history = stored?.history;

            if (
                !Array.isArray(history) ||
                !history.length
            ) {
                return null;
            }

            const storedResultUrl =
                stored?.resultUrl;

            const currentResultUrl =
                this.normaliseUrl(
                    window.location.href
                );

            // Prevent history from a different results page
            // being restored accidentally.
            if (
                storedResultUrl &&
                currentResultUrl &&
                storedResultUrl !== currentResultUrl
            ) {
                return null;
            }

            const lastNode =
                history[history.length - 1];

            if (!lastNode?.id) {
                return null;
            }

            return history;
        } catch (err) {
            return null;
        }
    };

    clearResultsHistory = () => {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            window.sessionStorage.removeItem(
                RESULTS_HISTORY_STORAGE_KEY
            );
        } catch (err) {
            // No further action is required.
        }
    };

    buildNode = data => ({
        id: data.questionID,
        question: data.questionText,
        dataComponentStep:
            data.dataComponentStep,

        answers: (
            data.availableAnswers || []
        ).map(answer => ({
            label: answer.answerText,
            answerID: answer.answerID,
            answerKey: answer.answerKey,
            nextQuestionId:
                answer.nextQuestionId,
            isDeadEnd: answer.isDeadEnd,
            url: answer.resultsPageUrl,
            isSearchResult:
                answer.isSearchResult,
        })),

        breadcrumbs: data.breadcrumbs || [],
        isDeadEnd: data.isDeadEnd,
    });

    setContainerRef = element => {
        this.containerRef = element;
    };

    isRestartVariant = () => {
        return (
            this.props.variant || ''
        ).toLowerCase() === 'restart';
    };

    scrollToWidget = () => {
        if (
            this.hasAutoScrolled ||
            !this.containerRef ||
            typeof window === 'undefined'
        ) {
            return;
        }

        this.hasAutoScrolled = true;

        window.requestAnimationFrame(() => {
            this.containerRef.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        });
    };

    setSupportingContentVisible = isVisible => {
        if (typeof document === 'undefined') {
            return;
        }

        const elements =
            document.querySelectorAll(
                '.guidance-search, .hero-banner__text-block p'
            );

        elements.forEach(element => {
            element.style.display =
                isVisible ? '' : 'none';
        });
    };

    restoreResultsHistory = history => {
        if (
            !Array.isArray(history) ||
            !history.length
        ) {
            return;
        }

        const displayedNode =
            history[history.length - 1];

        this.clearTransitionTimers();
        this.setSupportingContentVisible(false);
        this.hasAutoScrolled = false;

        this.setState(
            {
                open: true,
                started: true,
                history,
                displayedNode,
                loading: false,
                error: null,
                transitioning: false,
                fadeState: 'idle',
                openedFromResultsPage: true,
            },
            () => {
                this.setComponentStep(
                    this.state.history
                );

                this.scrollToWidget();
            }
        );
    };

    openQuestion = (
        questionId,
        { fromResultsPage = false } = {}
    ) => {
        if (!questionId) {
            return;
        }

        this.clearTransitionTimers();
        this.setSupportingContentVisible(false);
        this.hasAutoScrolled = false;
        this.skipAutoLoadBecauseUrlParam = true;

        this.setState(
            {
                open: true,
                started: false,
                loading: true,
                error: null,
                history: [],
                displayedNode: null,
                transitioning: false,
                fadeState: 'idle',
                openedFromResultsPage:
                    fromResultsPage,
            },
            async () => {
                this.scrollToWidget();

                try {
                    const node =
                        await this.fetchNode({
                            questionId,
                        });

                    const history = [node];

                    this.setState(
                        {
                            started: true,
                            history,
                            displayedNode: node,
                            loading: false,
                            error: null,
                            transitioning: false,
                            fadeState: 'idle',
                            openedFromResultsPage:
                                fromResultsPage,
                        },
                        () => {
                            this.setComponentStep(
                                this.state.history
                            );
                        }
                    );
                } catch (err) {
                    this.setState(
                        {
                            started: false,
                            loading: false,
                            error:
                                'Sorry, something went wrong while loading this step.',
                            openedFromResultsPage:
                                false,
                        },
                        () => {
                            this.setComponentStep([]);
                        }
                    );
                }
            }
        );
    };

    async fetchNode(payload = {}) {
        const apiUrl =
            `${GET_QUESTION_URL}?questionId=` +
            `${payload.questionId || ''}`;

        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Content-Type':
                    'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(
                `Request failed with status ${response.status}`
            );
        }

        const json = await response.json();

        if (
            !json ||
            !json.success ||
            !json.data
        ) {
            throw new Error(
                'Unexpected API response format'
            );
        }

        return this.buildNode(json.data);
    }

    async loadNode(payload = {}) {
        const { displayedNode } = this.state;

        const isInitialLoad =
            !displayedNode;

        this.clearTransitionTimers();

        this.setState({
            loading: true,
            error: null,
        });

        try {
            const node =
                await this.fetchNode(payload);

            if (isInitialLoad) {
                this.setState(
                    previousState => {
                        const history = [
                            ...previousState.history,
                            node,
                        ];

                        return {
                            history,
                            displayedNode: node,
                            transitioning: false,
                            fadeState: 'idle',
                            loading: false,
                            error: null,
                        };
                    },
                    () => {
                        this.setComponentStep(
                            this.state.history
                        );
                    }
                );

                return;
            }

            this.setState({
                transitioning: true,
                fadeState: 'out',
                loading: false,
                error: null,
            });

            this.transitionTimer =
                setTimeout(() => {
                    this.setState(
                        previousState => {
                            const history = [
                                ...previousState.history,
                                node,
                            ];

                            return {
                                history,
                                displayedNode: node,
                                fadeState: 'idle',
                                transitioning: true,
                                error: null,
                            };
                        },
                        () => {
                            this.setComponentStep(
                                this.state.history
                            );

                            this.fadeInDelayTimer =
                                setTimeout(() => {
                                    this.setState({
                                        fadeState:
                                            'in',
                                    });

                                    this.scrollToQuestionArea();

                                    this.transitionTimer =
                                        setTimeout(
                                            () => {
                                                this.setState(
                                                    {
                                                        transitioning:
                                                            false,
                                                        fadeState:
                                                            'idle',
                                                    }
                                                );
                                            },
                                            TRANSITION_MS
                                        );
                                }, FADE_IN_DELAY_MS);
                        }
                    );
                }, TRANSITION_MS);
        } catch (err) {
            this.clearTransitionTimers();

            this.setState(
                {
                    loading: false,
                    transitioning: false,
                    fadeState: 'idle',
                    error:
                        'Sorry, something went wrong while loading this step.',
                },
                () => {
                    this.setComponentStep([]);
                }
            );
        }
    }

    handleSelect = answer => {
        if (this.state.transitioning) {
            return;
        }

        if (answer.url) {
            this.saveResultsHistory(
                answer.url
            );

            // Do not append questionId to the
            // result-page URL.
            window.location.href = answer.url;
            return;
        }

        this.scrollToQuestionArea();

        this.loadNode({
            questionId:
                answer.nextQuestionId,
        });
    };

    goBack = () => {
        this.clearTransitionTimers();

        if (this.state.history.length > 1) {
            const newHistory =
                this.state.history.slice(0, -1);

            const previousNode =
                newHistory[
                newHistory.length - 1
                ];

            this.setState(
                {
                    history: newHistory,
                    displayedNode:
                        previousNode,
                    transitioning: false,
                    fadeState: 'idle',
                    error: null,
                    loading: false,
                },
                () => {
                    this.setComponentStep(
                        this.state.history
                    );

                    this.scrollToQuestionArea();
                }
            );

            return;
        }

        // A direct results-page visitor only has
        // the referring question loaded.
        //
        // Back closes the question and returns to
        // the result-page content.
        if (
            this.state.openedFromResultsPage
        ) {
            this.closeWidget();
        }
    };

    startOver = () => {
        this.clearTransitionTimers();
        this.clearResultsHistory();
        this.setComponentStep([]);

        this.setState(
            {
                history: [],
                displayedNode: null,
                transitioning: false,
                fadeState: 'idle',
                error: null,
                loading: false,
                openedFromResultsPage:
                    false,
            },
            () => {
                this.scrollToQuestionArea();
                this.loadNode();
            }
        );
    };

    closeWidget = event => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const elements =
            document.querySelectorAll(
                '.guidance-search, .hero-banner__text-block p'
            );

        elements.forEach(element => {
            element.style.display = '';
        });

        this.setSupportingContentVisible(true);
        this.clearTransitionTimers();
        this.setComponentStep([]);

        this.setState({
            ...this.initialState,
        });
    };

    openWidget = () => {
        this.setState({
            open: true,
        });
    };

    handleResultsBackClick = event => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const storedHistory =
            this.getResultsHistory();

        if (storedHistory) {
            this.restoreResultsHistory(
                storedHistory
            );

            return;
        }

        // Direct result-page visit:
        // load the referring question supplied by
        // the results-page backend.
        const referringQuestionId =
            this.getReferringQuestionId();

        if (!referringQuestionId) {
            return;
        }

        this.openQuestion(
            referringQuestionId,
            {
                fromResultsPage: true,
            }
        );
    };

    handleResultsRestartClick = event => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.clearTransitionTimers();
        this.clearResultsHistory();
        this.setComponentStep([]);
        this.setSupportingContentVisible(false);
        this.hasAutoScrolled = false;

        this.setState(
            {
                open: true,
                started: false,
                history: [],
                loading: false,
                error: null,
                displayedNode: null,
                transitioning: false,
                fadeState: 'idle',
                openedFromResultsPage:
                    false,
            },
            () => {
                this.startFlow();
            }
        );
    };

    handleClosedWidgetClick = () => {
        this.setSupportingContentVisible(false);
        this.openWidget();
    };

    startFlow = async () => {
        this.clearTransitionTimers();

        this.setState({
            loading: true,
            error: null,
            history: [],
            displayedNode: null,
            transitioning: false,
            fadeState: 'idle',
            openedFromResultsPage: false,
        });

        try {
            const node =
                await this.fetchNode();

            const history = [node];

            this.setState(
                {
                    started: true,
                    history,
                    displayedNode: node,
                    loading: false,
                    error: null,
                    transitioning: false,
                    fadeState: 'idle',
                    openedFromResultsPage:
                        false,
                },
                () => {
                    this.setComponentStep(
                        this.state.history
                    );
                }
            );
        } catch (err) {
            this.setState(
                {
                    loading: false,
                    error:
                        'Sorry, something went wrong while loading this step.',
                },
                () => {
                    this.setComponentStep([]);
                }
            );
        }
    };

    renderAnswers = node => {
        const { urgentHelpContent } =
            this.props;

        if (!node) {
            return null;
        }

        const hasAnswers =
            node.answers &&
            node.answers.length > 0;

        return (
            <>
                <div className="answer-list">
                    {hasAnswers ? (
                        node.answers.map(
                            (answer, index) => (
                                <button
                                    key={`${node.id}-${index}`}
                                    className="answer"
                                    onClick={() =>
                                        this.handleSelect(
                                            answer
                                        )
                                    }
                                    disabled={
                                        this.state
                                            .transitioning
                                    }
                                >
                                    <span className="answer__label">
                                        {
                                            answer.label
                                        }
                                    </span>

                                    <img
                                        className="answer__icon"
                                        src={
                                            answer.isSearchResult
                                                ? '/content/assets/general/img/ask-ash/Search_Purple.svg'
                                                : '/content/assets/general/img/ask-ash/Chevron_Dark.svg'
                                        }
                                        alt=""
                                    />
                                </button>
                            )
                        )
                    ) : (
                        <button
                            className="answer"
                            onClick={
                                this.startOver
                            }
                            disabled={
                                this.state
                                    .transitioning
                            }
                        >
                            Start over
                        </button>
                    )}
                </div>

                <div
                    className="ask-ash-urgent-help"
                    dangerouslySetInnerHTML={{
                        __html:
                            urgentHelpContent,
                    }}
                />
            </>
        );
    };

    render() {
        const {
            open,
            started,
            history,
            loading,
            error,
            displayedNode,
            fadeState,
            transitioning,
            openedFromResultsPage,
        } = this.state;

        const {
            title,
            description,
            titleWhenOpen,
            descriptionWhenOpen,
        } = this.props;

        const currentNode =
            displayedNode;

        const resultsHistory =
            this.getResultsHistory();

        const referringQuestionId =
            this.getReferringQuestionId();

        const hasResultsBackTarget =
            Boolean(
                resultsHistory ||
                referringQuestionId
            );

        const canGoBack =
            history.length > 1 ||
            openedFromResultsPage;

        return (
            <>
                {open ? (
                    <div
                        className="askAshContainer"
                        ref={
                            this.setContainerRef
                        }
                    >
                        {!started ? (
                            <div className="askAshCard">
                                <img
                                    className="ask-ash-hi-logo"
                                    src="/content/assets/general/img/ask-ash/Hi_Image.png"
                                    alt="Ask Ash logo"
                                />

                                <div>
                                    <div
                                        className="askAshCard__header"
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                titleWhenOpen,
                                        }}
                                    />

                                    <p className="askAshCard__content">
                                        {
                                            descriptionWhenOpen
                                        }
                                    </p>

                                    <button
                                        className="primary"
                                        onClick={
                                            this
                                                .startFlow
                                        }
                                        disabled={
                                            loading
                                        }
                                    >
                                        {loading
                                            ? 'Loading…'
                                            : 'Start now'}

                                        {!loading && (
                                            <img
                                                className="button-icon button-icon--right"
                                                src="/content/assets/general/img/ask-ash/Arrow_White.png"
                                                alt=""
                                            />
                                        )}
                                    </button>
                                </div>

                                <div
                                    className="close-widget"
                                    onClick={
                                        this
                                            .closeWidget
                                    }
                                >
                                    <img
                                        className="close-icon"
                                        src="/content/assets/general/img/ask-ash/Close_Dark.svg"
                                        alt="Close"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="askAshCard__nav">
                                <div className="nav">
                                    {canGoBack && (
                                        <button
                                            className="back-button"
                                            onClick={
                                                this
                                                    .goBack
                                            }
                                            disabled={
                                                transitioning
                                            }
                                        >
                                            <img
                                                className="button-icon"
                                                src="/content/assets/general/img/ask-ash/Arrow_White.png"
                                                alt=""
                                            />

                                            Back
                                        </button>
                                    )}

                                    <button
                                        className="start-over-button"
                                        onClick={
                                            this
                                                .startOver
                                        }
                                        disabled={
                                            transitioning
                                        }
                                    >
                                        <img
                                            className="button-icon"
                                            src="/content/assets/general/img/ask-ash/Restart_Dark.svg"
                                            alt=""
                                        />

                                        Restart
                                    </button>

                                    <div
                                        className="close-widget"
                                        onClick={
                                            this
                                                .closeWidget
                                        }
                                    >
                                        <img
                                            className="close-icon"
                                            src="/content/assets/general/img/ask-ash/Close_Dark.svg"
                                            alt="Close"
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="askAshError">
                                        {error}
                                    </div>
                                )}

                                {!currentNode &&
                                    loading ? (
                                    <div className="askAshCard__initialLoading">
                                        <div className="askAshCard__info">
                                            <h2>
                                                Loading…
                                            </h2>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className={`askAshCard__contentFade ${fadeState ===
                                                'out'
                                                ? 'is-fading-out'
                                                : fadeState ===
                                                    'in'
                                                    ? 'is-fading-in'
                                                    : ''
                                            }`}
                                    >
                                        <div className="askAshCard__info">
                                            <img
                                                className="ask-ash-question-logo"
                                                src="/content/assets/general/img/ask-ash/Ask_Ash_Logo.png"
                                                alt="Ask Ash logo"
                                            />

                                            <h2>
                                                {
                                                    currentNode?.question
                                                }
                                            </h2>
                                        </div>

                                        {currentNode &&
                                            this.renderAnswers(
                                                currentNode
                                            )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : this.isRestartVariant() ? (
                    <div className="askAshWidget askAshWidget--restart askAshWidget--resultsActions">
                        <button
                            type="button"
                            className="back-button askAshWidget__resultAction askAshWidget__resultAction--back"
                            onClick={
                                this
                                    .handleResultsBackClick
                            }
                            disabled={
                                !hasResultsBackTarget
                            }
                        >
                            <img
                                className="button-icon askAshWidget__resultActionIcon"
                                src="/content/assets/general/img/ask-ash/Arrow_White.png"
                                alt=""
                            />

                            <span>Back</span>
                        </button>

                        <button
                            type="button"
                            className="start-over-button askAshWidget__resultAction askAshWidget__resultAction--restart"
                            onClick={
                                this
                                    .handleResultsRestartClick
                            }
                        >
                            <img
                                className="button-icon askAshWidget__resultActionIcon"
                                src="/content/assets/general/img/ask-ash/Restart_Dark.svg"
                                alt=""
                            />

                            <span>
                                Restart
                            </span>
                        </button>
                    </div>
                ) : (
                    <div
                        className="askAshWidget"
                        onClick={
                            this
                                .handleClosedWidgetClick
                        }
                    >
                        <img
                            className="ask-ash-logo"
                            src="/content/assets/general/img/ask-ash/Ask_Ash_Logo.png"
                            alt="Ask Ash logo"
                        />

                        <div className="askAshWidget__text">
                            <p className="askAshWidget__title">
                                {title}
                            </p>

                            <div
                                dangerouslySetInnerHTML={{
                                    __html:
                                        description,
                                }}
                            />
                        </div>

                        <div>
                            <button
                                type="button"
                                className="inner-right__button button button--secondary"
                            >
                                Start
                                <span className="mobileHide">
                                    &nbsp;here{' '}
                                </span>
                                &#8594;
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    }
}
